// Build N-level group hierarchy from grouped metadata
// Input groupedMetadata shape: { groupName: { group_info, fields } }
// Output: array of nodes { name, groupInfo, fields:[{field, metadata}], children:[] }

const toBool = (val) => {
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return val !== 0;
  if (typeof val === "string") {
    return !["false", "0", "", "null", "undefined", "none"].includes(
      val.toLowerCase()
    );
  }
  return Boolean(val);
};

const matchesConditionValue = (val, required) => {
  if (Array.isArray(required)) {
    if (Array.isArray(val)) {
      return required.some((candidate) => val.includes(candidate));
    }
    return required.some((candidate) => matchesConditionValue(val, candidate));
  }
  if (required === null || required === undefined) {
    return val === null || val === undefined;
  }
  if (typeof required === "boolean") {
    return toBool(val) === required;
  }
  return String(val) === String(required);
};

const evalCondition = (cond, values) => {
  if (!cond) return null;
  let result = null;
  if (typeof cond === "string") {
    result = toBool(values?.[cond]);
  } else if (typeof cond === "object") {
    result = Object.entries(cond).every(([field, required]) => {
      const val = values?.[field];
      return matchesConditionValue(val, required);
    });
  }
  return result;
};

export const shouldShowField = (metadata, values) => {
  const appearance = metadata?.appearance;
  if (appearance === "hidden" || appearance === "readonly") return false;

  const { show_if, hide_if } = metadata || {};
  const showResult = evalCondition(show_if, values);
  const hideResult = evalCondition(hide_if, values);
  return !(showResult === false || hideResult === true);
};

export const sortFieldsByAppearanceOrder = (fields) => {
  return [...fields].sort(
    (a, b) => (a.metadata.appearance_order || 0) - (b.metadata.appearance_order || 0)
  );
};

export const buildGroupHierarchy = (groupedMetadata) => {
  if (!groupedMetadata) return [];
  const nodesByName = {};
  // First pass: create nodes
  Object.entries(groupedMetadata).forEach(([groupName, groupData]) => {
    const groupInfo = groupData?.group_info || {};
    const fields = Object.entries(groupData?.fields || {}).map(([field, metadata]) => ({ field, metadata }));
    nodesByName[groupName] = {
      name: groupName,
      groupInfo,
      fields,
      children: [],
    };
  });
  // Second pass: link children by father_group
  const roots = [];
  Object.values(nodesByName).forEach((node) => {
    const fatherName = node.groupInfo?.father_group;
    if (fatherName && nodesByName[fatherName]) {
      nodesByName[fatherName].children.push(node);
    } else {
      roots.push(node);
    }
  });
  // Sort groups by order if present
  const sortByOrder = (a, b) => (a.groupInfo?.order || 999) - (b.groupInfo?.order || 999);
  const sortRec = (arr) => {
    arr.sort(sortByOrder);
    arr.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
};

// Compute visible field counts recursively, and filter out empty groups unless flagged keepEmpty
export const annotateVisibilityAndFilter = (nodes, values, keepEmptyNames = new Set()) => {
  const walk = (node) => {
    const visibleFields = sortFieldsByAppearanceOrder(node.fields).filter(({ metadata }) => shouldShowField(metadata, values));
    const children = node.children.map(walk).filter(Boolean);
    const childCount = children.reduce((acc, c) => acc + (c.visibleCount || 0), 0);
    const selfCount = visibleFields.length;
    const total = selfCount + childCount;
    const result = { ...node, fields: visibleFields, children, visibleCount: total };
    if (total > 0 || keepEmptyNames.has(node.name)) return result;
    return null;
  };
  return nodes.map(walk).filter(Boolean);
};


